import { modifierDiceGroups, modifierDiceTotal, readModifierDice } from '../../../shared/rules/effects/rollDiceModifiers.mjs';
import { offerSaveRescue } from '../utils/strokeOfLuckPrompt';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { rollDiceWithBox, DICE_BOX_ROLL_SURFACE_EVENT } from '../../../utils/diceBoxManager';
import { notify } from '../../../utils/notification';
import CombatSavePrompt from './CombatSavePrompt';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';

export default function ConcentrationSaveControls({ campaignId, characterId, character, onCombatChange, beforeRoll, onRollResult }) {
  const [decisions, setDecisions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const evidence = useRef(new Map()); const lock = useRef(false);
  const base = `/campaigns/${encodeURIComponent(campaignId || '')}/combat/concentration`;
  const load = useCallback(async () => {
    const response = await apiFetch(`${base}/sync`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(characterId ? { characterId } : {}) });
    if (!response.ok) throw new Error('Concentration checks could not be loaded.');
    return (await response.json()).decisions || [];
  }, [base, characterId]);
  useCampaignSpellSync({ campaignId, kind: 'concentration', load, onData: next =>
    setDecisions(previous => JSON.stringify(previous) === JSON.stringify(next) ? previous : next) });
  useEffect(() => {
    evidence.current.clear(); setDecisions([]); setError('');
  }, [campaignId, characterId]);
  const decision = decisions[0];
  const answer = async pass => {
    if (!decision || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      let body = evidence.current.get(decision.id);
      if (body && body.pass !== pass) throw new Error('Retry your original concentration choice.');
      if (!body) {
        body = { pass };
        if (!pass) {
          if (beforeRoll) await beforeRoll();
          else { window.dispatchEvent(new Event(DICE_BOX_ROLL_SURFACE_EVENT)); await new Promise(resolve => requestAnimationFrame(resolve)); }
          const roll = await rollDiceWithBox([{ count: decision.rollMode === 'normal' ? 1 : 2, sides: 20 }, ...modifierDiceGroups(decision.diceModifiers)]);
          const dice = roll?.rolls?.[0];
          if (!Array.isArray(dice) || dice.length !== (decision.rollMode === 'normal' ? 1 : 2) || dice.some(die => !Number.isInteger(die) || die < 1 || die > 20)) throw new Error('Visible concentration dice did not finish rolling.');
          body.dice = [...dice];
          if (decision.diceModifiers?.length) body.modifierDice = readModifierDice(roll, decision.diceModifiers);
          onRollResult?.(roll);
          // Stroke of Luck (Rogue 20) or Indomitable (Fighter 9) can rescue a failed save.
          const d20 = decision.rollMode === 'advantage' ? Math.max(...dice) : Math.min(...dice);
          const modifier = Number(decision.modifier) + modifierDiceTotal(decision.diceModifiers, body.modifierDice || []);
          if (Number.isFinite(modifier) && d20 + modifier < decision.dc) Object.assign(body, await offerSaveRescue(character, { id: decision.id, what: 'Concentration saving throw', outcome: `${20 + modifier} vs DC ${decision.dc}` }));
        }
        evidence.current.set(decision.id, body);
      }
      const response = await apiFetch(`${base}/${encodeURIComponent(decision.id)}/respond`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json(); if (!response.ok) throw new Error(result.message || 'Concentration check could not be submitted.');
      if (result.combat) onCombatChange(previous => ({ ...previous, activeEffects: result.combat.activeEffects || [] }));
      window.dispatchEvent(new Event('web:changed'));
      notify(result.cancelled ? 'This concentration check is no longer needed.' : result.success ? `Concentration maintained: ${result.total} vs DC ${result.dc}.` : 'Concentration ended.', result.cancelled ? 'info' : result.success ? 'success' : 'danger');
      setDecisions(previous => previous.filter(item => item.id !== decision.id && (result.success || item.concentrationId !== decision.concentrationId)));
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!decision || busy) return null;
  return <CombatSavePrompt opportunity={{ resolutionId: decision.id,
    sourceEffect: { displayName: `Concentration: ${decision.spellName} (${decision.damage} damage)` },
    targetSave: { ability: 'constitution', dc: decision.dc, targetCombatantId: decision.targetCombatantId, targetName: decision.targetName, ...(decision.rollMode !== 'normal' ? { rollMode: decision.rollMode } : {}) } }}
    error={error} onRoll={() => answer(false)} onPass={() => answer(true)} />;
}
